import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { QuickDateRange } from '../../components/ui/DateField'
import { downloadXlsx } from '../../lib/downloadXlsx'

interface ReconciliationRun {
  id: number
  type: string
  channel: string | null
  periodStart: string
  periodEnd: string
  startedAt: string
  finishedAt: string | null
  totalChecked: number
  matchedOk: number
  mismatches: number
  trigger: string
}

interface ReconciliationResult {
  id: number
  runId: number
  status: string
  sourceType: string
  sourceRef: string
  sourceDate: string | null
  eaRef: string | null
  diffSummary: string | null
  severity: string
  resolved: boolean
  resolvedAt: string | null
  notes: string | null
  createdAt: string
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  ok: { label: 'OK', color: 'bg-b4l-green-light text-b4l-green-dark' },
  missing_in_ea: { label: 'Lipsește în EA', color: 'bg-b4l-red-light text-b4l-red-dark' },
  missing_invoice: { label: 'Comandă fără factură', color: 'bg-b4l-red-light text-b4l-red-dark' },
  value_mismatch: { label: 'Diferență valoare', color: 'bg-b4l-amber-light text-b4l-amber-dark' },
  missing_in_source: { label: 'Lipsește în eMAG', color: 'bg-b4l-amber-light text-b4l-amber-dark' },
  commission_missing: {
    label: 'Factură Dante doar în SPV',
    color: 'bg-b4l-amber-light text-b4l-amber-dark',
  },
}

// Explicatii scurte pentru categoriile mai putin evidente (afisate cand filtrezi pe ele).
const STATUS_HELP: Record<string, string> = {
  missing_in_ea: 'Factură eMAG care nu se regăsește în ExpertAccounts — de înregistrat în EA.',
  missing_invoice: 'Comandă eMAG livrată fără factură de vânzare emisă.',
  value_mismatch: 'Aceeași factură există în ambele, dar cu valori diferite — verifică suma.',
  missing_in_source: 'Factură de vânzare în EA (serie fac_emag) fără echivalent în eMAG.',
  commission_missing:
    'Factură primită de la Dante International (eMAG) prin e-Factura, vizibilă în SPV, dar care NU apare în facturile sincronizate din portalul eMAG. De obicei sunt facturi cu serie proprie Dante (nu comision MKTP) care trebuie verificate și înregistrate în contabilitate (EA).',
}

const SEVERITY_DOT: Record<string, string> = {
  info: 'bg-b4l-text-muted',
  warn: 'bg-b4l-amber-dark',
  critical: 'bg-b4l-red-dark',
}

export function VerificareEmagPage() {
  const navigate = useNavigate()
  const permissions = useAuthStore((s) => s.permissions)
  const canRun = permissions.includes('compliance:run')

  const today = new Date()
  const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0)
  const [periodStart, setPeriodStart] = useState(prevMonthStart.toISOString().slice(0, 10))
  const [periodEnd, setPeriodEnd] = useState(prevMonthEnd.toISOString().slice(0, 10))

  const runsQuery = useQuery<{ runs: ReconciliationRun[] }>({
    queryKey: ['emag-verif-runs'],
    queryFn: async () =>
      (await api.get('/compliance/runs?type=channel_ea&channel=emag&limit=20')).data,
    refetchInterval: 30_000,
  })

  const runMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post('/compliance/run', {
          type: 'channel_ea',
          channel: 'emag',
          periodStart,
          periodEnd,
        })
      ).data,
    onSuccess: (d: unknown) => {
      const r = d as { runId: number; queued?: boolean }
      toast.success(`Rulare #${r.runId} pornită — rulează în fundal, se actualizează în 30-60 sec.`)
      runsQuery.refetch()
      navigate(`/emag/verificare/runs/${r.runId}`)
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare'
      toast.error(message)
    },
  })

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">eMAG → Verificare facturi (eMAG ↔ EA)</h1>

      <Card>
        <p className="text-xs text-b4l-text-muted">
          Reconciliază facturile de vânzare eMAG (DSMF…) cu documentele <b>fac_emag</b> din
          ExpertAccounts (gol webAPI, comenzi fără factură, diferențe de valoare){' '}
          <b>+ comisioanele Dante</b> (primite prin e-Factura în SPV) cu facturile de comision eMAG
          (MKTP). Recomandat: rulează pe o <b>lună închisă</b> (unde EA e complet sincronizat).
        </p>
      </Card>

      <Card title="Rulează verificare pe perioadă">
        <div className="space-y-3">
          <div className="max-w-xl">
            <QuickDateRange
              fromValue={periodStart}
              toValue={periodEnd}
              onChange={(f, t) => {
                setPeriodStart(f)
                setPeriodEnd(t)
              }}
            />
          </div>
          {canRun && (
            <Button onClick={() => runMutation.mutate()} loading={runMutation.isPending}>
              Rulează verificarea
            </Button>
          )}
        </div>
      </Card>

      <Card title="Istoric rulări">
        {runsQuery.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
        {runsQuery.data?.runs?.length === 0 && (
          <p className="text-sm text-b4l-text-muted">Nicio rulare încă.</p>
        )}
        {runsQuery.data?.runs && runsQuery.data.runs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                <tr>
                  <th className="py-2 pr-3">ID</th>
                  <th className="py-2 pr-3">Perioadă</th>
                  <th className="py-2 pr-3">Pornită de</th>
                  <th className="py-2 pr-3">Verificate</th>
                  <th className="py-2 pr-3">OK</th>
                  <th className="py-2 pr-3">Diferențe</th>
                  <th className="py-2 pr-3">Pornit</th>
                  <th className="py-2 pr-3">Finalizat</th>
                </tr>
              </thead>
              <tbody>
                {runsQuery.data.runs.map((r) => (
                  <tr
                    key={r.id}
                    className="cursor-pointer border-b border-b4l-border hover:bg-b4l-bg-soft"
                    onClick={() => navigate(`/emag/verificare/runs/${r.id}`)}
                  >
                    <td className="py-2 pr-3 font-mono">#{r.id}</td>
                    <td className="py-2 pr-3">
                      {new Date(r.periodStart).toLocaleDateString('en-GB')} -{' '}
                      {new Date(r.periodEnd).toLocaleDateString('en-GB')}
                    </td>
                    <td className="py-2 pr-3">{r.trigger}</td>
                    <td className="py-2 pr-3">{r.totalChecked}</td>
                    <td className="py-2 pr-3 text-b4l-green-dark">{r.matchedOk}</td>
                    <td className="py-2 pr-3 text-b4l-red-dark">{r.mismatches}</td>
                    <td className="py-2 pr-3">{new Date(r.startedAt).toLocaleString('en-GB')}</td>
                    <td className="py-2 pr-3">
                      {r.finishedAt ? new Date(r.finishedAt).toLocaleString('en-GB') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

export function VerificareEmagRunDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canRun = permissions.includes('compliance:run')
  const [statusFilter, setStatusFilter] = useState<string>('')

  const { data, isLoading } = useQuery<{
    run: ReconciliationRun
    results: ReconciliationResult[]
    summary: Record<string, number>
  }>({
    queryKey: ['emag-verif-detail', id, statusFilter],
    queryFn: async () =>
      (
        await api.get(
          `/compliance/runs/${id}/results${statusFilter ? `?status=${statusFilter}` : ''}`,
        )
      ).data,
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.run?.finishedAt ? false : 5000),
  })

  const resolveMutation = useMutation({
    mutationFn: async (resultId: number) =>
      (await api.patch(`/compliance/results/${resultId}/resolve`, {})).data,
    onSuccess: () => {
      toast.success('Marcat rezolvat')
      queryClient.invalidateQueries({ queryKey: ['emag-verif-detail', id] })
    },
  })

  const unresolveMutation = useMutation({
    mutationFn: async (resultId: number) =>
      (await api.patch(`/compliance/results/${resultId}/unresolve`, {})).data,
    onSuccess: () => {
      toast.success('Rezolvare anulată')
      queryClient.invalidateQueries({ queryKey: ['emag-verif-detail', id] })
    },
  })

  const summary = data?.summary ?? {}

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 md:gap-3">
        <Button variant="secondary" onClick={() => navigate('/emag/verificare')}>
          ← Înapoi
        </Button>
        <h1 className="text-base font-semibold sm:text-xl">Verificare eMAG — rulare #{id}</h1>
        <div className="ml-auto flex gap-2">
          <Button
            variant="secondary"
            onClick={() =>
              downloadXlsx(
                `/compliance/runs/${id}/export${statusFilter ? `?status=${statusFilter}` : ''}`,
              ).catch(() => toast.error('Eroare la export'))
            }
          >
            Export XLSX{statusFilter ? ' (filtru)' : ''}
          </Button>
        </div>
      </div>

      {data?.run && (
        <Card>
          <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <div>
              <div className="text-xs text-b4l-text-muted">Perioadă</div>
              <div>
                {new Date(data.run.periodStart).toLocaleDateString('en-GB')} —{' '}
                {new Date(data.run.periodEnd).toLocaleDateString('en-GB')}
              </div>
            </div>
            <div>
              <div className="text-xs text-b4l-text-muted">Verificate</div>
              <div className="font-semibold">{data.run.totalChecked}</div>
            </div>
            <div>
              <div className="text-xs text-b4l-text-muted">OK</div>
              <div className="font-semibold text-b4l-green-dark">{data.run.matchedOk}</div>
            </div>
            <div>
              <div className="text-xs text-b4l-text-muted">Diferențe</div>
              <div className="font-semibold text-b4l-red-dark">{data.run.mismatches}</div>
            </div>
          </div>
          {!data.run.finishedAt && (
            <p className="mt-2 text-xs text-b4l-amber-dark">
              Rulează în fundal… se actualizează automat.
            </p>
          )}
        </Card>
      )}

      <Card title="Filtre">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setStatusFilter('')}
            className={`rounded border px-3 py-1 text-xs ${
              !statusFilter
                ? 'border-b4l-text bg-b4l-text text-white'
                : 'border-b4l-border bg-white'
            }`}
          >
            Toate ({Object.values(summary).reduce<number>((a, b) => a + b, 0)})
          </button>
          {Object.entries(STATUS_LABELS).map(([k, info]) => (
            <button
              key={k}
              onClick={() => setStatusFilter(k)}
              className={`rounded border px-3 py-1 text-xs ${
                statusFilter === k
                  ? 'border-b4l-text bg-b4l-text text-white'
                  : 'border-b4l-border bg-white'
              }`}
            >
              {info.label} ({summary[k] ?? 0})
            </button>
          ))}
        </div>
        {statusFilter && STATUS_HELP[statusFilter] && (
          <p className="mt-3 rounded-md bg-b4l-bg-soft px-3 py-2 text-xs text-b4l-text-muted">
            {STATUS_HELP[statusFilter]}
          </p>
        )}
      </Card>

      <Card title="Rezultate (max 1000 afișate)">
        {isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
        {data?.results?.length === 0 && (
          <p className="text-sm text-b4l-text-muted">Nicio înregistrare pentru filtrul curent.</p>
        )}
        {data?.results && data.results.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                <tr>
                  <th className="py-2 pr-3">!</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Referință (eMAG)</th>
                  <th className="py-2 pr-3">EA ref</th>
                  <th className="py-2 pr-3">Data</th>
                  <th className="py-2 pr-3">Detalii</th>
                  <th className="py-2 pr-3">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => {
                  const info = STATUS_LABELS[r.status] ?? {
                    label: r.status,
                    color: 'bg-b4l-bg-soft',
                  }
                  return (
                    <tr key={r.id} className="border-b border-b4l-border">
                      <td className="py-2 pr-3">
                        <span
                          className={`inline-block h-2 w-2 rounded-full ${SEVERITY_DOT[r.severity] ?? 'bg-b4l-text-muted'}`}
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`rounded px-2 py-0.5 ${info.color}`}>{info.label}</span>
                      </td>
                      <td className="py-2 pr-3 font-mono">{r.sourceRef}</td>
                      <td className="py-2 pr-3 font-mono">{r.eaRef ?? '—'}</td>
                      <td className="py-2 pr-3">
                        {r.sourceDate ? new Date(r.sourceDate).toLocaleDateString('en-GB') : '—'}
                      </td>
                      <td className="py-2 pr-3">{r.diffSummary ?? '—'}</td>
                      <td className="py-2 pr-3">
                        {r.resolved ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="text-b4l-green-dark">✓ rezolvat</span>
                            {canRun && (
                              <button
                                type="button"
                                onClick={() => unresolveMutation.mutate(r.id)}
                                disabled={unresolveMutation.isPending}
                                className="text-xs text-b4l-text-muted underline hover:text-b4l-text disabled:opacity-60"
                              >
                                anulează
                              </button>
                            )}
                          </span>
                        ) : (
                          r.status !== 'ok' &&
                          canRun && (
                            <Button
                              variant="secondary"
                              onClick={() => resolveMutation.mutate(r.id)}
                              loading={resolveMutation.isPending}
                            >
                              Rezolvă
                            </Button>
                          )
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
